import React, { useCallback, useEffect, useState } from "react";
import { Bell, X, AlertTriangle, ClipboardX, UserX, ChevronRight } from "lucide-react";
import { supabase } from "../../supabaseClient";

// ══════════════════════════════════════════════════════════════════
// DashboardAlertsWidget.jsx — "Phase 1" of the in-dashboard chatbot
// Bakri asked for (2026-11-04): a floating bell/chat widget, always
// available, that surfaces the same three warnings the email-based
// edge functions already detect — overdue tasks, missing Daily
// Activity (3 working days), 3 consecutive absences — but live, inside
// the dashboard itself, instead of only by email.
//
// Scope rule mirrors App.jsx's own `fullAccess` array exactly
// (['CEO','Program Manager','Operations Manager'] — kept in sync
// deliberately, not imported, since App.jsx defines it twice inline
// rather than exporting it): those positions see alerts across every
// active project; everyone else (Supervisor, Site Engineer, Project
// Manager, …) sees only their own `assigned_projects`.
//
// Detection logic is a client-side port of:
//   - send-notification (task overdue)
//   - daily-activity-warning (missing Daily Activity, 3 working days)
//   - absence-warning (3 consecutive Absent attendance records)
// Kept numerically identical to those edge functions so a project
// flagged here is always the same one that would trigger an email.
//
// "Phase 2" (ask-a-question AI assistant, pending an Anthropic API
// key) will live alongside this widget, not replace it.
// ══════════════════════════════════════════════════════════════════

const FULL_ACCESS_POSITIONS = ['CEO', 'Program Manager', 'Operations Manager'];
const REFRESH_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
const ABSENCE_LOOKBACK_DAYS = 14; // wide enough to reliably catch each member's 3 most recent attendance rows

function getPreviousWorkingDays(n) {
    const days = [];
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - 1);
    while (days.length < n) {
        const dow = d.getDay();
        if (dow !== 5 && dow !== 6) days.push(d.toISOString().split('T')[0]);
        d.setDate(d.getDate() - 1);
    }
    return days;
}

function fmtShortDate(iso) {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function AlertBubble({ theme, color, icon: Icon, title, body, meta, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`w-full text-left rounded-xl border ${theme.border} p-3 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors flex gap-2.5 items-start`}
        >
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${color}1A` }}>
                <Icon size={14} color={color} />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-[11px] font-black uppercase tracking-wide" style={{ color }}>{title}</p>
                <p className={`text-xs font-bold ${theme.text} mt-0.5 truncate`}>{body}</p>
                {meta && <p className={`text-[10px] ${theme.subtext} mt-0.5`}>{meta}</p>}
            </div>
            <ChevronRight size={14} className={`${theme.subtext} flex-shrink-0 mt-1`} />
        </button>
    );
}

export default function DashboardAlertsWidget({ currentUser, theme, darkMode, onNavigate }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [alerts, setAlerts] = useState({ overdue: [], missingReports: [], absences: [] });
    const [lastChecked, setLastChecked] = useState(null);

    const position = currentUser?.position;
    const isFullAccess = FULL_ACCESS_POSITIONS.includes(position);
    const assignedKey = (currentUser?.assigned_projects || []).join(',');

    const fetchAlerts = useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);
        try {
            let projQuery = supabase.from('projects').select('id,name').eq('status', 'Active');
            if (!isFullAccess) {
                const scoped = assignedKey ? assignedKey.split(',') : [];
                if (!scoped.length) {
                    setAlerts({ overdue: [], missingReports: [], absences: [] });
                    setLastChecked(new Date());
                    setLoading(false);
                    return;
                }
                projQuery = projQuery.in('id', scoped);
            }
            const { data: projects } = await projQuery;
            const projectIds = (projects || []).map(p => p.id);
            const projectMap = {};
            (projects || []).forEach(p => { projectMap[p.id] = p.name; });

            if (!projectIds.length) {
                setAlerts({ overdue: [], missingReports: [], absences: [] });
                setLastChecked(new Date());
                setLoading(false);
                return;
            }

            // ── 1) Overdue tasks (mirrors send-notification) ──
            const { data: tasks } = await supabase
                .from('tasks')
                .select('id,sub_task,system,assigned_to,deadline_date,project_id,status')
                .neq('status', 'Completed')
                .in('project_id', projectIds);

            const today = new Date();
            const overdue = (tasks || [])
                .map(t => {
                    if (!t.deadline_date) return null;
                    const diffDays = Math.ceil((new Date(t.deadline_date).getTime() - today.getTime()) / 86400000);
                    if (diffDays >= 0) return null;
                    return { ...t, daysLate: Math.abs(diffDays), projectName: projectMap[t.project_id] || t.project_id };
                })
                .filter(Boolean)
                .sort((a, b) => b.daysLate - a.daysLate);

            // ── 2) Missing Daily Activity, 3 working days (mirrors daily-activity-warning) ──
            const checkDates = getPreviousWorkingDays(3);
            const { data: activities } = await supabase
                .from('daily_activities')
                .select('project_id,date')
                .in('project_id', projectIds)
                .in('date', checkDates);

            const submittedByProject = {};
            (activities || []).forEach(a => {
                (submittedByProject[a.project_id] ||= new Set()).add(a.date);
            });
            const missingReports = projectIds
                .filter(pid => checkDates.every(d => !submittedByProject[pid]?.has(d)))
                .map(pid => ({ projectId: pid, projectName: projectMap[pid] || pid, dates: checkDates }));

            // ── 3) 3 consecutive absences (mirrors absence-warning) ──
            const since = new Date();
            since.setDate(since.getDate() - ABSENCE_LOOKBACK_DAYS);
            const { data: attendance } = await supabase
                .from('attendance')
                .select('member_id,project_id,date,status')
                .in('project_id', projectIds)
                .gte('date', since.toISOString().split('T')[0])
                .order('date', { ascending: false })
                .limit(3000);

            const byMemberProject = {};
            (attendance || []).forEach(r => {
                const key = `${r.member_id}__${r.project_id}`;
                (byMemberProject[key] ||= []).push(r);
            });
            const flagged = Object.values(byMemberProject)
                .filter(rows => rows.length >= 3 && rows.slice(0, 3).every(r => r.status === 'Absent'))
                .map(rows => ({ memberId: rows[0].member_id, projectId: rows[0].project_id, dates: rows.slice(0, 3).map(r => r.date) }));

            let absences = [];
            if (flagged.length) {
                const memberIds = [...new Set(flagged.map(f => f.memberId))];
                const { data: members } = await supabase.from('team_members').select('id,name,position').in('id', memberIds);
                const memberMap = {};
                (members || []).forEach(m => { memberMap[m.id] = m; });
                absences = flagged.map(f => ({
                    ...f,
                    memberName: memberMap[f.memberId]?.name || `#${f.memberId}`,
                    position: memberMap[f.memberId]?.position || '',
                    projectName: projectMap[f.projectId] || f.projectId,
                }));
            }

            setAlerts({ overdue, missingReports, absences });
            setLastChecked(new Date());
        } catch (err) {
            console.error('DashboardAlertsWidget: fetch failed', err);
        } finally {
            setLoading(false);
        }
        // assignedKey (not the array itself) keeps this stable across re-renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, isFullAccess, assignedKey]);

    useEffect(() => {
        fetchAlerts();
        const interval = setInterval(fetchAlerts, REFRESH_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [fetchAlerts]);

    if (!currentUser) return null;

    const totalCount = alerts.overdue.length + alerts.missingReports.length + alerts.absences.length;

    return (
        <>
            <button
                onClick={() => setOpen(o => !o)}
                style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}
                className="w-14 h-14 rounded-full bg-blue-600 shadow-xl flex items-center justify-center hover:scale-105 transition-transform"
                aria-label="Alerts"
            >
                <Bell size={22} className="text-white" />
                {totalCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center border-2 border-white dark:border-gray-900">
                        {totalCount > 9 ? '9+' : totalCount}
                    </span>
                )}
            </button>

            {open && (
                <div
                    style={{ position: 'fixed', bottom: 92, right: 24, zIndex: 1000, width: 360, maxHeight: '70vh' }}
                    className={`${theme.card} rounded-2xl border ${theme.border} shadow-2xl flex flex-col overflow-hidden`}
                >
                    <div className="flex items-center justify-between px-4 py-3 bg-blue-600 flex-shrink-0">
                        <div className="flex items-center gap-2">
                            <Bell size={16} className="text-white" />
                            <span className="text-sm font-black text-white">Alerts{isFullAccess ? ' — All Projects' : ''}</span>
                        </div>
                        <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white" aria-label="Close">
                            <X size={18} />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                        {loading && totalCount === 0 && (
                            <p className={`text-xs text-center py-6 ${theme.subtext}`}>Checking…</p>
                        )}

                        {!loading && totalCount === 0 && (
                            <div className="text-center py-10">
                                <p className={`text-sm font-bold ${theme.text}`}>All clear</p>
                                <p className={`text-xs ${theme.subtext} mt-1`}>No overdue tasks, missing reports, or absences.</p>
                            </div>
                        )}

                        {alerts.overdue.map((t, i) => (
                            <AlertBubble
                                key={`ov-${t.id || i}`}
                                theme={theme}
                                color="#ef4444"
                                icon={AlertTriangle}
                                title={`Task overdue by ${t.daysLate} day${t.daysLate !== 1 ? 's' : ''}`}
                                body={t.sub_task}
                                meta={`${t.projectName}${t.assigned_to ? ' · ' + t.assigned_to : ''}`}
                                onClick={() => { onNavigate?.(t.project_id, 'Tasks'); setOpen(false); }}
                            />
                        ))}

                        {alerts.missingReports.map((m, i) => (
                            <AlertBubble
                                key={`mr-${m.projectId}-${i}`}
                                theme={theme}
                                color="#f97316"
                                icon={ClipboardX}
                                title="Daily Report missing — 3 working days"
                                body={m.projectName}
                                meta={m.dates.slice().reverse().map(fmtShortDate).join(', ')}
                                onClick={() => { onNavigate?.(m.projectId, 'Daily Activities'); setOpen(false); }}
                            />
                        ))}

                        {alerts.absences.map((a, i) => (
                            <AlertBubble
                                key={`ab-${a.memberId}-${a.projectId}-${i}`}
                                theme={theme}
                                color="#dc2626"
                                icon={UserX}
                                title="3 consecutive absent days"
                                body={`${a.memberName}${a.position ? ' · ' + a.position : ''}`}
                                meta={`${a.projectName} · ${a.dates.slice().reverse().map(fmtShortDate).join(', ')}`}
                                onClick={() => { onNavigate?.(a.projectId, 'Team'); setOpen(false); }}
                            />
                        ))}
                    </div>

                    <div className={`px-4 py-2 border-t ${theme.border} flex items-center justify-between flex-shrink-0`}>
                        <span className={`text-[10px] ${theme.subtext}`}>
                            {lastChecked ? `Updated ${lastChecked.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : ''}
                        </span>
                        <button onClick={fetchAlerts} disabled={loading} className="text-[10px] font-bold text-blue-600 hover:underline disabled:opacity-50">
                            Refresh
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
